import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Avatar,
  Alert,
  CircularProgress,
  IconButton,
  Button,
  SelectChangeEvent
} from '@mui/material';
import {
  Search,
  Warning,
  MonitorHeart,
  Favorite,
  FitnessCenter,
  ArrowBack,
  Phone,
  CheckCircle,
  Refresh,
  Email,
  LocalHospital,
  CalendarToday
} from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getCacheBustedImageUrl } from '../../utils/s3Utils';

const API_URL = process.env.REACT_APP_API_URL;

interface PatientStatusItem {
  id: string;
  patient_id: string;
  name: string;
  age: number;
  gender: string;
  phone: string;
  email: string;
  vitals_reading?: string;
  doctor_name?: string;
  emergency_contact_phone?: string;
  profile_image?: string;
  medical_history?: string;
  allergies?: string;
  is_active: boolean;
  created_at: string;
  subscription_end_date?: string;
}

export default function PatientStatusListPage() {
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Mode detection: Status (Doctor) vs Category (Admin)
  const healthStatusParam = searchParams.get('status');
  const activityParam = searchParams.get('activity');
  const categoryParam = searchParams.get('category');

  const isStatusMode = !!(healthStatusParam || activityParam);
  const isCategoryMode = !!categoryParam || (!isStatusMode);

  // Initialize filters
  const initialStatus = healthStatusParam || 'critical';
  const initialActivity = activityParam || 'heartrate';
  const initialCategory = (categoryParam || 'male_patients') as 'male_patients' | 'female_patients' | 'active_subscribers' | 'doctors_no_patients' | 'new_patients';

  const [patients, setPatients] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activityFilter, setActivityFilter] = useState<string>(initialActivity);
  const [healthStatus, setHealthStatus] = useState<string>(initialStatus);
  const [selectedCategory, setSelectedCategory] = useState<typeof initialCategory>(initialCategory);

  // Pagination
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);

  // Pagination mode: Status is client-side in the user code, Category is server-side in disk code.
  // I will make both "server-side" style to respect API limit/skip.

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');

      if (!token) {
        setError('Authentication token not found');
        return;
      }

      const params = new URLSearchParams();
      let endpoint = '';

      if (isStatusMode) {
        endpoint = `${API_URL}/dashboard/patients-by-status`;
        params.append('health_status', healthStatus);
        params.append('activity_status', activityFilter);
        // Note: The status API provided in user prompt doesn't seem to support limit/skip directly in the URL shown, 
        // but I'll add them just in case or handle slicing on frontend.
      } else {
        endpoint = `${API_URL}/admin/dashboard/category-data`;
        params.append('category', selectedCategory);
        params.append('limit', paginationModel.pageSize.toString());
        params.append('skip', (paginationModel.page * paginationModel.pageSize).toString());
        if (searchTerm) params.append('search', searchTerm);
      }

      const response = await fetch(`${endpoint}?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'accept': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch patients');
      }

      const data = await response.json();

      if (isStatusMode) {
        const patientList = data.patients_with_data || data.patients || (Array.isArray(data) ? data : []);

        const transformed: PatientStatusItem[] = patientList.map((item: any, index: number) => {
          const p = item.patient || item;
          const vitalsObj = item.latest_vitals || {};
          const activityVitals = vitalsObj[activityFilter] || {};
          const hrData = vitalsObj.heartrate || vitalsObj.heart_rate || item.heart_rate_data || {};
          const bpData = vitalsObj.bp || vitalsObj.blood_pressure || item.blood_pressure_data || {};
          const bmiData = vitalsObj.bmi || item.bmi_data || {};

          const getTimestamp = () => {
            if (activityFilter === 'heartrate') {
              if (hrData.heart_rate_created_date && hrData.heart_rate_created_time) {
                return `${hrData.heart_rate_created_date}T${hrData.heart_rate_created_time}`;
              }
              if (hrData.recorded_at) return hrData.recorded_at;
            } else if (activityFilter === 'bp') {
              if (bpData.bp_created_date && bpData.bp_created_time) {
                return `${bpData.bp_created_date}T${bpData.bp_created_time}`;
              }
              if (bpData.recorded_at) return bpData.recorded_at;
            } else if (activityFilter === 'bmi') {
              if (bmiData.bmi_created_date && bmiData.bmi_created_time) {
                return `${bmiData.bmi_created_date}T${bmiData.bmi_created_time}`;
              }
              if (bmiData.recorded_at) return bmiData.recorded_at;
            }

            return activityVitals.recorded_at || vitalsObj.recorded_at || item.recorded_at || item.created_at || p.created_at || new Date().toISOString();
          };

          let vitalsReading = 'N/A';
          if (activityFilter === 'heartrate') {
            const hr = hrData.heart_rate || item.heart_rate || activityVitals.heart_rate;
            vitalsReading = hr ? `${hr} bpm` : 'N/A';
          } else if (activityFilter === 'bp') {
            const s = bpData.systolic_bp || item.systolic_bp || activityVitals.systolic_bp;
            const d = bpData.diastolic_bp || item.diastolic_bp || activityVitals.diastolic_bp;
            vitalsReading = (s && d) ? `${s}/${d} mmHg` : 'N/A';
          } else if (activityFilter === 'bmi') {
            const b = bmiData.bmi || item.bmi || activityVitals.bmi;
            vitalsReading = b ? `${parseFloat(b).toFixed(1)}` : 'N/A';
          }

          return {
            id: item.id || item._id || `${p.id || p._id || 'patient'}_${index}`,
            patient_id: p.patient_id || '',
            name: p.name || cleanName(p.full_name) || 'Unknown',
            age: p.age || 0,
            gender: p.gender || '',
            phone: p.phone || '',
            email: p.email || '',
            vitals_reading: vitalsReading,
            doctor_name: p.doctor_name || '',
            emergency_contact_phone: p.emergency_contact_phone || '',
            profile_image: p.profile_image || undefined,
            medical_history: p.medical_history || '',
            allergies: p.allergies || '',
            is_active: p.is_active !== undefined ? p.is_active : true,
            created_at: getTimestamp(),
          };
        });

        setPatients(transformed);
        setRowCount(transformed.length);
      } else {
        // Category mode
        if (selectedCategory === 'doctors_no_patients') {
          setPatients(data.data || []);
          setRowCount(data.total || (data.data ? data.data.length : 0));
        } else {
          setPatients(data.patients_with_data || []);
          setRowCount(data.total_patients || (data.patients_with_data ? data.patients_with_data.length : 0));
        }
      }
    } catch (err: any) {
      console.error('Error fetching patients:', err);
      setError(err.message || 'Failed to load patients');
      setPatients([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  }, [isStatusMode, healthStatus, activityFilter, selectedCategory, paginationModel.page, paginationModel.pageSize, searchTerm]);

  // Utility to clean names
  const cleanName = (name: any) => {
    if (!name) return '';
    return String(name).replace(/\bNone\b/g, '').replace(/\s+/g, ' ').trim();
  };

  useEffect(() => {
    if (authLoading || !user) return;
    fetchData();
  }, [fetchData, authLoading, user]);

  // Sync with URL params
  useEffect(() => {
    const status = searchParams.get('status');
    const activity = searchParams.get('activity');
    const category = searchParams.get('category');

    if (status) setHealthStatus(status);
    if (activity) setActivityFilter(activity);
    if (category) setSelectedCategory(category as any);
  }, [searchParams]);

  const handleStatusChange = (event: SelectChangeEvent) => {
    const newStatus = event.target.value;
    setHealthStatus(newStatus);
    setSearchParams({ status: newStatus, activity: activityFilter });
  };

  const handleCategoryChange = (event: SelectChangeEvent) => {
    const newCategory = event.target.value;
    setSelectedCategory(newCategory as any);
    setSearchParams({ category: newCategory });
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  const filteredPatients = useMemo(() => {
    if (!searchTerm || (isCategoryMode)) return patients; // Filtering handled server-side for categories
    const term = searchTerm.toLowerCase();
    return patients.filter(p =>
      (p.name || '').toLowerCase().includes(term) ||
      (p.email || '').toLowerCase().includes(term) ||
      (p.phone || '').includes(term)
    );
  }, [patients, searchTerm, isCategoryMode]);

  const getFilterLabel = (filter: string) => {
    switch (filter) {
      case 'heartrate': return 'Heart Rate';
      case 'bp': return 'Blood Pressure';
      case 'bmi': return 'BMI';
      default: return filter;
    }
  };

  const getPageTitle = () => {
    if (isStatusMode) {
      switch (healthStatus) {
        case 'critical': return 'Needs Priority Review';
        case 'warning': return 'Needs Review';
        case 'normal': return 'Within Usual Range';
        default: return 'Patient Status List';
      }
    } else {
      switch (selectedCategory) {
        case 'male_patients': return 'Male Patients Distribution';
        case 'female_patients': return 'Female Patients Distribution';
        case 'active_subscribers': return 'Active Subscribers';
        case 'doctors_no_patients': return 'Doctors with No Patients';
        case 'new_patients': return 'New Patients (This Week)';
        default: return 'Patient Distribution';
      }
    }
  };

  const getStatusColor = () => {
    switch (healthStatus) {
      case 'critical': return '#d84315';
      case 'warning': return '#ef6c00';
      case 'normal': return '#2e7d32';
      default: return '#1976d2';
    }
  };

  const columns: GridColDef[] = useMemo(() => {
    if (isStatusMode) {
      return [
        {
          field: 'name',
          headerName: 'Patient Name',
          width: 250,
          renderCell: (params) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar
                src={getCacheBustedImageUrl(params.row.profile_image)}
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: params.row.profile_image ? 'transparent' : 'primary.main'
                }}
              >
                {!params.row.profile_image && (params.value?.charAt(0) || 'U')}
              </Avatar>
              <Typography variant="body2">{params.value || 'Unknown'}</Typography>
            </Box>
          )
        },
        { field: 'age', headerName: 'Age', width: 80 },
        {
          field: 'gender',
          headerName: 'Gender',
          width: 100,
          valueFormatter: (value: any) => {
            if (!value) return '';
            return String(value).charAt(0).toUpperCase() + String(value).slice(1);
          }
        },
        {
          field: 'vitals_reading',
          headerName: getFilterLabel(activityFilter),
          width: 150,
          renderCell: (params) => (
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: getStatusColor() }}>
              {params.value}
            </Typography>
          )
        },
        {
          field: 'emergency_contact_phone',
          headerName: 'Emergency Contact',
          width: 180,
          renderCell: (params) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Phone sx={{ fontSize: '1rem', color: 'error.main' }} />
              <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                {params.value || 'N/A'}
              </Typography>
            </Box>
          )
        },
        {
          field: 'status',
          headerName: 'Health Status',
          width: 200,
          renderCell: () => {
            const title = getPageTitle();
            const color = getStatusColor();
            const bgColor = healthStatus === 'critical' ? '#ffccbc' : healthStatus === 'warning' ? '#ffe0b2' : '#c8e6c9';

            return (
              <Chip
                label={title}
                size="small"
                icon={healthStatus === 'normal' ? <CheckCircle /> : <Warning />}
                sx={{
                  bgcolor: bgColor,
                  color: color,
                  fontWeight: 'bold',
                  '& .MuiChip-icon': {
                    color: color
                  }
                }}
              />
            );
          }
        },
        {
          field: 'created_at',
          headerName: 'Inserted Date',
          width: 180,
          renderCell: (params) => {
            try {
              const val = params.value;
              if (!val) return 'N/A';
              return new Date(val).toLocaleString([], {
                year: 'numeric', month: '2-digit', day: '2-digit',
                hour: '2-digit', minute: '2-digit', hour12: true
              });
            } catch { return 'N/A'; }
          }
        }
      ];
    } else {
      // Category mode columns
      if (selectedCategory === 'doctors_no_patients') {
        return [
          {
            field: 'full_name',
            headerName: 'Doctor Name',
            width: 220,
            renderCell: (params) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar src={getCacheBustedImageUrl(params.row.profile_image)} sx={{ width: 32, height: 32, bgcolor: 'info.main' }}>
                  {!params.row.profile_image && (params.value?.charAt(0) || 'D')}
                </Avatar>
                <Typography variant="body2">{cleanName(params.value) || 'Unknown'}</Typography>
              </Box>
            )
          },
          { field: 'specialization', headerName: 'Specialization', width: 200 },
          { field: 'years_of_experience', headerName: 'Experience', width: 150, renderCell: (params) => <Typography variant="body2">{params.value || 0} years</Typography> },
          { field: 'email', headerName: 'Email Address', flex: 1, minWidth: 200, renderCell: (params) => <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Email sx={{ fontSize: '1rem', color: 'text.secondary' }} /><Typography variant="body2">{params.value || 'N/A'}</Typography></Box> },
          {
            field: 'is_active',
            headerName: 'Account Status',
            width: 140,
            renderCell: (params) => (
              <Chip label={params.value ? "ACTIVE" : "INACTIVE"} size="small" color={params.value ? "success" : "error"} sx={{ fontWeight: 'bold', minWidth: 80 }} />
            )
          }
        ];
      } else {
        // Patient category columns
        const cols: GridColDef[] = [
          {
            field: 'name',
            headerName: 'Patient Name',
            flex: 0.8,
            minWidth: 160,
            valueGetter: (value, row) => cleanName(row.full_name || value),
            renderCell: (params) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar src={getCacheBustedImageUrl(params.row.profile_image)} sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                  {!params.row.profile_image && (params.value?.charAt(0) || 'P')}
                </Avatar>
                <Typography variant="body2">{params.value || 'Unknown'}</Typography>
              </Box>
            )
          },
          { field: 'age', headerName: 'Age', width: 70 },
          { field: 'gender', headerName: 'Gender', width: 100 },
          { field: 'phone', headerName: 'Phone', width: 170, renderCell: (params) => <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Phone sx={{ fontSize: '1rem', color: 'text.secondary' }} /><Typography variant="body2">{params.value || 'N/A'}</Typography></Box> },
          { field: 'doctor_name', headerName: 'Assigned Doctor', flex: 0.5, minWidth: 120, renderCell: (params) => <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><LocalHospital sx={{ fontSize: '1rem', color: 'primary.main' }} /><Typography variant="body2">{params.value || 'Unassigned'}</Typography></Box> }
        ];

        if (selectedCategory === 'active_subscribers') {
          cols.push({
            field: 'subscription_end_date',
            headerName: 'Subscription End Date',
            width: 180,
            renderCell: (params) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CalendarToday sx={{ fontSize: '1rem', color: 'secondary.main' }} />
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>{params.value ? new Date(params.value).toLocaleDateString() : 'N/A'}</Typography>
              </Box>
            )
          });
        }

        cols.push({
          field: 'is_active',
          headerName: 'Status',
          width: 110,
          renderCell: (params) => <Chip label={params.value ? "Active" : "Inactive"} size="small" color={params.value ? "success" : "default"} variant="outlined" />
        });

        return cols;
      }
    }
  }, [isStatusMode, healthStatus, activityFilter, selectedCategory]);

  if (authLoading || !user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => navigate(-1)} color="primary">
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" fontWeight="bold">
          {getPageTitle()} {isStatusMode && `- ${getFilterLabel(activityFilter)}`}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 0.8fr' },
            gap: 2,
            alignItems: 'center'
          }}>
            <TextField
              fullWidth
              placeholder={`Search patients by name...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><Search /></InputAdornment>
                )
              }}
            />
            {isStatusMode ? (
              <FormControl fullWidth>
                <InputLabel>Health Status</InputLabel>
                <Select value={healthStatus} label="Health Status" onChange={handleStatusChange}>
                  <MenuItem value="critical">Needs Priority Review</MenuItem>
                  <MenuItem value="warning">Needs Review</MenuItem>
                  <MenuItem value="normal">Within Usual Range</MenuItem>
                </Select>
              </FormControl>
            ) : (
              <FormControl fullWidth>
                <InputLabel>Distribution Category</InputLabel>
                <Select value={selectedCategory} label="Distribution Category" onChange={handleCategoryChange}>
                  <MenuItem value="male_patients">Male Patients</MenuItem>
                  <MenuItem value="female_patients">Female Patients</MenuItem>
                </Select>
              </FormControl>
            )}
            <Button
              fullWidth variant="outlined" startIcon={<Refresh />}
              onClick={() => {
                setSearchTerm('');
                if (isStatusMode) setHealthStatus('critical');
                else setSelectedCategory('male_patients');
                setSearchParams({});
              }}
              sx={{ height: '56px' }}
            >
              Reset
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Alert (Specific to Status Mode) */}
      {isStatusMode && filteredPatients.length > 0 && (
        <Alert severity={healthStatus === 'normal' ? 'success' : healthStatus === 'warning' ? 'warning' : 'error'} sx={{ mb: 3 }}>
          <strong>{filteredPatients.length}</strong> patient(s) found for {getPageTitle()}
        </Alert>
      )}

      {/* Data Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            {getPageTitle()}
          </Typography>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
              <CircularProgress />
            </Box>
          ) : (
            <DataGrid
              rows={filteredPatients}
              columns={columns}
              paginationMode={isCategoryMode ? "server" : "client"}
              rowCount={rowCount}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[5, 10, 25, 50]}
              disableRowSelectionOnClick
              autoHeight
              sx={{
                border: 'none',
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#e3f2fd',
                  cursor: 'pointer'
                }
              }}
            />
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
